import React from 'react';
import PropTypes from 'prop-types';

import { StickyContainer, Sticky } from 'react-sticky';
import VisibilitySensor from 'react-visibility-sensor';

import SpinnerContainer from 'app/shared/components/SpinnerContainer';

import TableMFHHeader from './TableMFHHeader';
import TableMFHRow from './TableMFHRow';

import './style.scss';

const noop = () => { };

const propTypes = {
    columnConfig: PropTypes.arrayOf(PropTypes.shape({
        width: PropTypes.number.isRequired,
        dataKey: PropTypes.string,
        header: PropTypes.string.isRequired,
        isSortable: PropTypes.bool,
        renderer: PropTypes.func,
        align: PropTypes.oneOf(['left', 'right', 'center']),
        headerRenderer: PropTypes.func,
        className: PropTypes.string,
        sortType: PropTypes.oneOf(['string', 'number']),
    })),
    data: PropTypes.array,
    filters: PropTypes.object,
    selected: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
    onSelectRow: PropTypes.func,
    onSaveFilters: PropTypes.func,
    TopBannerComponent: PropTypes.func,
    ExpandedComponent: PropTypes.func,
    FilterComponent: PropTypes.func,
    handleLoadMoreData: PropTypes.func,
    hasMoreData: PropTypes.bool,
    isLoading: PropTypes.bool,
    groupingFunction: PropTypes.func,
    groupingHeaderRenderer: PropTypes.func,
    isMobile: PropTypes.bool,
    ErrorRowComponent: PropTypes.func,
    selectionDisabled: PropTypes.bool,
    defaultSortKey: PropTypes.object,
    rowSelectionDisabled: PropTypes.func,
    isRowExpandable: PropTypes.func,
    stickyHeader: PropTypes.bool,
    edgePadding: PropTypes.number,
};

const defaultProps = {
    columnConfig: [],
    data: [],
    filters: new Set(),
    selected: new Set(),
    onSaveFilters: noop,
    onSelectRow: noop,
    TopBannerComponent: noop,
    ExpandedComponent: noop,
    FilterComponent: noop,
    handleLoadMoreData: noop,
    hasMoreData: false,
    isLoading: false,
    groupingFunction: null,
    groupingHeaderRenderer: () => { },
    isMobile: false,
    ErrorRowComponent: noop,
    selectionDisabled: false,
    defaultSortKey: null,
    rowSelectionDisabled: () => false,
    isRowExpandable: noop,
    stickyHeader: true,
    edgePadding: 0,
};

const TableMFH = ({
    columnConfig,
    data,
    filters,
    selected,
    onSaveFilters,
    onSelectRow,
    TopBannerComponent,
    ExpandedComponent,
    FilterComponent,
    handleLoadMoreData,
    hasMoreData,
    isLoading,
    groupingFunction,
    groupingHeaderRenderer,
    isMobile,
    ErrorRowComponent,
    selectionDisabled,
    defaultSortKey,
    rowSelectionDisabled,
    isRowExpandable,
    stickyHeader,
    edgePadding,
}) => {
    const canSelectRow = onSelectRow !== noop;
    const showTopBanner = TopBannerComponent !== noop;
    const showErrorRow = ErrorRowComponent !== noop;
    const canFilter = FilterComponent !== noop;
    const hasExpandableRows = isRowExpandable !== noop && ExpandedComponent !== noop;

    let columnDivisions = columnConfig.reduce((val, col) => val + col.width, 0);
    if (canSelectRow) {
        columnDivisions += 1;
    }
    if (canFilter || hasExpandableRows) {
        columnDivisions += 1;
    }

    const [sortKey, setSortKey] = React.useState(defaultSortKey);
    const [expanded, setExpanded] = React.useState(new Set());
    const [isFilterOpen, setFilterState] = React.useState(false);

    const handleToggleExpanded = (i) => {
        const newExpanded = new Set([...expanded].filter(index => index === i));
        if (newExpanded.has(i)) {
            newExpanded.delete(i);
        } else {
            newExpanded.add(i);
        }
        setExpanded(newExpanded);
    };

    let processedData = data;
    if (sortKey) {
        processedData = data.slice(0);
        const sortConfig = columnConfig.find(col => col.dataKey === sortKey.key);
        const { sortType } = sortConfig;
        processedData.sort((a, b) => {
            const aValue = a[sortKey.key];
            const bValue = b[sortKey.key];
            if (aValue === bValue) return 0;
            if (sortKey.ascending) {
                if (aValue === undefined || aValue === null) return -1;
                if (bValue === undefined || bValue === null) return 1;
                if (sortType === 'string') {
                    return aValue.localeCompare(bValue, undefined, { numeric: true });
                }
                return aValue > bValue ? 1 : -1;
            }
            if (bValue === undefined || bValue === null) return -1;
            if (aValue === undefined || aValue === null) return 1;
            if (sortType === 'string') {
                return bValue.localeCompare(aValue, undefined, { numeric: true });
            }
            return aValue < bValue ? 1 : -1;
        });
    }
    const selectedIndexes = typeof selected === 'function' ? selected(processedData) : selected;
    const handleFilterToggle = () => setFilterState(!isFilterOpen);

    const updateFilters = (newFilters) => {
        onSaveFilters(newFilters);
        setFilterState(false);
    };

    const canExpandCurrentRow = (rowData) => {
        if (!hasExpandableRows) {
            return false;
        }
        return isRowExpandable(rowData);
    };

    const groupingMap = {
        indexToGroup: {},
        groupToRows: {},
        groupToIndex: {},
    };
    if (groupingFunction) {
        processedData.forEach((rowData, i) => {
            const groupId = groupingFunction(rowData);
            groupingMap.indexToGroup[i] = groupId;
            groupingMap.groupToRows[groupId] = groupingMap.groupToRows[groupId] || [];
            groupingMap.groupToRows[groupId].push(data[i]);
            groupingMap.groupToIndex[groupId] = groupingMap.groupToIndex[groupId] || [];
            groupingMap.groupToIndex[groupId].push(i);
        });
    }

    const handleSelectRow = (i) => {
        const newSelected = new Set(selectedIndexes);
        if (newSelected.has(i)) {
            newSelected.delete(i);
        } else {
            newSelected.add(i);
        }
        onSelectRow(newSelected, processedData);
    };

    const handleSelectAll = () => {
        const newSelected = new Set();
        if (selectedIndexes.size === 0) {
            for (let i = 0; i < data.length; i++) {
                if (!rowSelectionDisabled(data[i])) {
                    newSelected.add(i);
                }
            }
        }
        onSelectRow(newSelected, processedData);
    };

    const handleGroupToggle = (groupId) => {
        const newSelected = new Set(selectedIndexes);
        let selectedCount = 0;
        groupingMap.groupToIndex[groupId].forEach((i) => {
            if (newSelected.has(i)) selectedCount += 1;
        });
        groupingMap.groupToIndex[groupId].forEach((i) => {
            if (selectedCount === 0) {
                if (!rowSelectionDisabled(data[i])) {
                    newSelected.add(i);
                }
            } else {
                newSelected.delete(i);
            }
        });
        onSelectRow(newSelected, processedData);
    };

    const isGroupSelected = (groupId) => {
        let isSelected = true;
        groupingMap.groupToIndex[groupId].forEach((i) => {
            isSelected = isSelected && selectedIndexes.has(i);
        });
        return isSelected;
    };

    const renderRows = () => processedData.map((rowData, i) => (
        <TableMFHRow
            key={i}
            columnConfig={columnConfig}
            data={rowData}
            isSelected={selectedIndexes.has(i)}
            isExpanded={expanded.has(i)}
            canSelectRow={canSelectRow}
            canExpandCurrentRow={canExpandCurrentRow(rowData)}
            hasExpandableRows={hasExpandableRows}
            canFilter={canFilter}
            onToggleSelected={() => handleSelectRow(i)}
            onToggleExpanded={() => handleToggleExpanded(i)}
            ExpandedComponent={ExpandedComponent}
            columnDivisions={columnDivisions}
            selectionDisabled={selectionDisabled}
            rowSelectionDisabled={rowSelectionDisabled}
            edgePadding={edgePadding}
        />
    ));

    const isGroupDisabled = currentGroup => groupingMap.groupToIndex[currentGroup]
        .every(i => rowSelectionDisabled(data[i]));

    const renderGroupedRows = () => {
        const rows = [];
        let currentGroup;
        processedData.forEach((rowData, i) => {
            if (groupingMap.indexToGroup[i] !== currentGroup) {
                currentGroup = groupingMap.indexToGroup[i];
                rows.push(
                    <TableMFHRow
                        key={`group_header_${currentGroup}`}
                        canSelectRow={canSelectRow}
                        data={{
                            groupRows: groupingMap.groupToRows[currentGroup],
                        }}
                        isSelected={isGroupSelected(currentGroup)}
                        onToggleSelected={() => handleGroupToggle(groupingMap.indexToGroup[i])}
                        columnConfig={
                            [{
                                width: columnDivisions - 2, // -1 for the select box column
                                header: '',
                                renderer: groupingHeaderRenderer,
                            }]
                        }
                        columnDivisions={columnDivisions}
                        selectionDisabled={isGroupDisabled(currentGroup)}
                        rowSelectionDisabled={() => false}
                        hasExpandableRows={hasExpandableRows}
                        edgePadding={edgePadding}
                    />
                );
            }
            rows.push(
                <TableMFHRow
                    key={i}
                    columnConfig={columnConfig}
                    data={rowData}
                    isSelected={selectedIndexes.has(i)}
                    isExpanded={expanded.has(i)}
                    canSelectRow={canSelectRow}
                    canExpandCurrentRow={isRowExpandable(rowData)}
                    canFilter={canFilter}
                    onToggleSelected={() => handleSelectRow(i)}
                    onToggleExpanded={() => handleToggleExpanded(i)}
                    ExpandedComponent={ExpandedComponent}
                    columnDivisions={columnDivisions}
                    selectionDisabled={selectionDisabled}
                    rowSelectionDisabled={rowSelectionDisabled}
                    hasExpandableRows={hasExpandableRows}
                    edgePadding={edgePadding}
                />
            );
        });
        return rows;
    };

    const handleSortClick = (key) => {
        if (sortKey && sortKey.key === key) {
            setSortKey({
                key,
                ascending: !sortKey.ascending,
            });
        } else {
            setSortKey({
                key,
                ascending: true,
            });
        }
    };

    const renderTableHeader = ({ style } = {}) => (
        <div className="TableMFH-sticky-area" style={style}>
            {showTopBanner && (
                <TopBannerComponent
                    selected={selected}
                    data={data}
                />
            )}
            {canFilter && (
                <FilterComponent
                    isFilterOpen={isFilterOpen}
                    handleFilterToggle={handleFilterToggle}
                    handleSaveFilters={updateFilters}
                    initialFilters={filters}
                />
            )}
            <TableMFHHeader
                columnConfig={columnConfig}
                sortKey={sortKey}
                isSelected={selectedIndexes.size === processedData.length && processedData.length > 0}
                isFilterOpen={isFilterOpen}
                canFilter={canFilter}
                canSelectRow={canSelectRow}
                onSelectToggle={handleSelectAll}
                onFilterToggle={handleFilterToggle}
                onSortClick={handleSortClick}
                isMobile={isMobile}
                filters={filters}
                columnDivisions={columnDivisions}
                selectionDisabled={selectionDisabled}
                hasExpandableRows={hasExpandableRows}
                edgePadding={edgePadding}
            />
        </div>
    );

    const renderTableContent = () => (
        <>
            {groupingFunction ? renderGroupedRows() : renderRows()}
            {isLoading && <SpinnerContainer customClass="TableMFH-data-row" />}
            {showErrorRow && <ErrorRowComponent />}
            {hasMoreData && !isLoading && (
                <VisibilitySensor onChange={handleLoadMoreData} />
            )}
        </>
    );


    return (
        <div className="TableMFH">
            {stickyHeader
                ? (
                    <StickyContainer>
                        <Sticky>
                            {renderTableHeader}
                        </Sticky>
                        {renderTableContent()}
                    </StickyContainer>
                )
                : (
                    <>
                        {renderTableHeader()}
                        {renderTableContent()}
                    </>
                )}
        </div>
    );
};

TableMFH.propTypes = propTypes;
TableMFH.defaultProps = defaultProps;

export default TableMFH;
